/**
 * Adjust a size indicator (sm, md, etc.) of a given string based on its length
 * @param value {string}
 * @param size {string} xs, sm, md, lg, xl
 * @param baseCharCount {number} the expected optimal character length
 * @param shiftUp {boolean} whether to shift the size up
 * @param shiftDown {boolean} whether to shift the size down
 * @returns {string} the new size indicator
 */
export function adaptSize ( value, size = "md", baseCharCount = 60, shiftUp = true, shiftDown = true ) {
  let result = size;
  const sizes = ["xs", "sm", "md", "lg"];
  const topSizeIndex = sizes.length - 1;
  const sizeIndex = sizes.indexOf( size );

  const largeModifier = Number.parseInt( baseCharCount / 1.5, 10 );
  const smallModifier = Number.parseInt( largeModifier / 1.5, 10 );

  // lower outer-quartile range ( 0% -> 25% )
  // if it's shorter than this, try to shift up two sizes
  const lowOut = smallModifier;

  // lower inter-quartile range ( 25% -> 50% )
  // if it's shorter this, try to shift up one size
  const lowIn = largeModifier;

  // upper inter-quartile range ( 50% -> 75% )
  // if it's longer than this, try to shift down one size
  const upIn = baseCharCount + smallModifier;

  // upper outer-quartile range ( 75% -> 100% )
  // if it's longer than this, try to shift down two sizes
  const upOut = baseCharCount + largeModifier;

  // general null check
  if ( value && sizeIndex !== -1 ) {
    const { length } = value;

    // if the size isn't at min
    if ( shiftDown && sizeIndex > 0 ) {
      // if the value is shorter than the outer-quartile range
      // and can be shifted by two indexes, shift by two indexes
      // otherwise, shift by one index
      if ( length >= upOut && sizeIndex >= 2 ) {
        result = sizes[sizeIndex - 2];
      } else if ( length >= upIn ) {
        result = sizes[sizeIndex - 1];
      }
    }

    // if the size isn't at max
    if ( shiftUp && sizeIndex < topSizeIndex ) {
      // if the value is longer than the upper outer-quartile range
      // and can be shifted by two indexes, shift by two indexes
      // otherwise, shift by one index
      if ( length <= lowOut && sizeIndex <= topSizeIndex - 2 ) {
        result = sizes[sizeIndex + 2];
      } else if ( length <= lowIn ) result = sizes[sizeIndex + 1];
    }
  }

  return result;
}

/**
 * Shift a size indicator down ( xl -> lg, xl -> md, etc. ) of a given string based on its length
 * @param value {string}
 * @param size {string} xs, sm, md, lg, xl
 * @param baseCharCount {number} the expected optimal character length
 * @returns {string} the new size indicator
 */
export function adaptSizeDown ( value, size, baseCharCount ) {
  return adaptSize( value, size, baseCharCount, false );
}

/**
 * shift a size indicator up ( xs -> sm, xs -> md, etc. ) of a given string based on its length
 * @param value {string}
 * @param size {string} xs, sm, md, lg, xl
 * @param baseCharCount {number} the expected optimal character length
 * @returns {string} the new size indicator
 */
export function adaptSizeUp ( value, size, baseCharCount ) {
  return adaptSize( value, size, baseCharCount, true, false );
}
